import { Component } from "react";
import { reportClientError } from "../utils/api";
import { BG, CARD, CARD2, BD, TX, TX2, GR, BL, RD } from "../utils/constants";

function isNetworkError(error) {
  if (!error) return false;
  const msg = error.message || "";
  return (
    msg.includes("Network Error") ||
    msg.includes("ECONNREFUSED") ||
    msg.includes("Failed to fetch") ||
    msg.includes("ERR_CONNECTION_REFUSED") ||
    msg.includes("net::ERR") ||
    (error.code === "ERR_NETWORK")
  );
}

function ErrorScreen({ title, subtitle, detail, onRetry }) {
  return (
    <div className="ts-scan-shell" style={{ minHeight: "100vh", background: BG, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'IBM Plex Mono', monospace", padding: 24 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=Space+Grotesk:wght@800&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
      `}</style>

      <div className="ts-panel" style={{ position: "relative", background: CARD, border: `1px solid ${BD}`, borderRadius: 14, padding: "40px 36px", maxWidth: 480, width: "100%", animation: "fadeUp 0.35s ease-out", boxShadow: "0 32px 80px rgba(0,0,0,0.6)" }}>
        {/* Icon */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: "50%", background: `${RD}14`, border: `1px solid ${RD}40`, marginBottom: 16 }}>
            <span style={{ fontSize: 24 }}>⚠</span>
          </div>
          <div className="ts-gradient-text" style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 900, fontSize: 20, letterSpacing: 2, marginBottom: 10 }}>
            Trade Scanner
          </div>
          <div className="ts-loading-dots" style={{ justifyContent: "center" }}><i /><i /><i /></div>
        </div>

        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: TX, marginBottom: 8, fontFamily: "'Space Grotesk', sans-serif" }}>{title}</div>
          <div style={{ fontSize: 10, color: TX2, lineHeight: 1.7 }}>{subtitle}</div>
        </div>

        {/* Detail box */}
        {detail && (
          <div style={{ background: CARD2, border: `1px solid ${BD}`, borderRadius: 10, padding: "11px 14px", marginBottom: 20, fontSize: 9, color: TX2, lineHeight: 1.6, wordBreak: "break-word" }}>
            <span style={{ color: RD, fontSize: 8, letterSpacing: 1 }}>ERROR · </span>{detail}
          </div>
        )}

        {/* Retry button */}
        <button
          onClick={onRetry}
          style={{ width: "100%", background: `linear-gradient(135deg,${GR},${BL})`, border: "none", borderRadius: 10, padding: "13px 0", color: "#000", fontSize: 11, fontWeight: 800, cursor: "pointer", letterSpacing: 2, fontFamily: "'IBM Plex Mono', monospace", marginBottom: 14 }}
          onMouseOver={(e) => e.currentTarget.style.filter = "brightness(1.1)"}
          onMouseOut={(e) => e.currentTarget.style.filter = ""}
        >
          RETRY →
        </button>

        <div style={{ textAlign: "center", fontSize: 9, color: TX2 }}>
          If the problem persists, make sure the server is running on port 3001.
        </div>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info);
    reportClientError(error, { componentStack: info?.componentStack, boundary: "ErrorBoundary" });
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
    // Hard reload if retry doesn't help after boundary reset
    setTimeout(() => {
      if (this.state.hasError) window.location.reload();
    }, 2000);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error } = this.state;
    const offline = isNetworkError(error);

    if (offline) {
      return (
        <ErrorScreen
          title="Server Offline"
          subtitle="Trade Scanner can't reach the backend server. Make sure the server is running and try again."
          detail={error?.message}
          onRetry={this.handleRetry}
        />
      );
    }

    return (
      <ErrorScreen
        title="Something went wrong"
        subtitle="An unexpected error occurred. This has been noted. Click Retry to reload the app."
        detail={error?.message}
        onRetry={this.handleRetry}
      />
    );
  }
}
